import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { Palette, Download, Sparkles, ArrowLeft, RotateCcw } from "lucide-react";
import EtOHStudioPublisher from "@/components/ContentStudioPublisher";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VARIANTS = [
  { v: "fb", label: "Facebook / LinkedIn", w: 1200, h: 630 },
  { v: "feed", label: "Instagram + LinkedIn Feed", w: 1080, h: 1350 },
  { v: "story", label: "Instagram Story", w: 1080, h: 1920 },
  { v: "li", label: "LinkedIn post carré", w: 1200, h: 627 },
];

const COLOR_PRESETS = [
  { name: "Fond La WineTech (défaut)", c1: "", c2: "" },
  { name: "Bordeaux / Navy", c1: "722F37", c2: "263064" },
  { name: "Rosé / Wine", c1: "E33170", c2: "722F37" },
  { name: "Or / Bordeaux", c1: "C8A04E", c2: "722F37" },
  { name: "Vert / Bleu nuit", c1: "0F766E", c2: "1E1B4B" },
  { name: "Sable / Crème", c1: "C8B58A", c2: "5C4A2E" },
];

const POLE_TAG_PRESETS = [
  "Pôle Producteur",
  "Pôle Consommateur",
  "Pôle On-Trade",
  "Pôle Off-Trade",
  "Pôle Logistique",
];

const PROMO_PRESETS = ["-20% adhésion", "Offre limitée", "Premier mois offert", "Save the date", "À ne pas manquer"];

export default function AdminMemberVisuals() {
  const { slug } = useParams();
  const [ctx, setCtx] = useState(null);
  const [variant, setVariant] = useState("feed");
  const [c1, setC1] = useState("");
  const [c2, setC2] = useState("");
  const [slogan, setSlogan] = useState("");
  const [tag, setTag] = useState("");
  const [promo, setPromo] = useState("");
  const [contactId, setContactId] = useState("");

  useEffect(() => {
    api.get(`/admin/members/${slug}/visual-context`).then((r) => {
      setCtx(r.data);
      const primary = r.data.contacts.find((c) => c.is_primary_contact) || r.data.contacts[0];
      if (primary) setContactId(primary.id);
      setSlogan(r.data.company.tagline || (r.data.company.description || "").split(". ")[0].slice(0, 160));
      // Sprint 92 — Default tag = "Pôle {X}" based on the company sector
      if (r.data.company.pole_label) setTag(r.data.company.pole_label);
    }).catch(() => {});
  }, [slug]);

  const previewUrl = useMemo(() => {
    const params = new URLSearchParams({ variant });
    if (c1) params.set("c1", c1);
    if (c2) params.set("c2", c2);
    if (slogan) params.set("slogan", slogan);
    if (tag) params.set("tag", tag);
    if (promo) params.set("promo", promo);
    if (contactId) params.set("contact_id", contactId);
    return `${BACKEND}/api/visuals/member/${slug}.png?${params.toString()}`;
  }, [slug, variant, c1, c2, slogan, tag, promo, contactId]);

  const cw = variant === "feed" || variant === "story" ? 1080 : 1200;
  const ch = variant === "feed" ? 1350 : variant === "story" ? 1920 : variant === "li" ? 627 : 630;
  const ratio = cw / ch;
  const reset = () => {
    setC1(""); setC2("");
    setTag(ctx?.company?.pole_label || "");
    setPromo("");
    setSlogan(ctx?.company?.tagline || (ctx?.company?.description || "").split(". ")[0].slice(0, 160) || "");
  };

  if (!ctx) return <PortalLayout sidebar="admin"><div className="py-12 text-center text-neutral-500">Chargement…</div></PortalLayout>;

  return (
    <PortalLayout sidebar="admin">
      <Link to="/admin/members" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-wine-700"><ArrowLeft className="h-3 w-3" />Retour aux membres</Link>
      <h1 className="mt-2 text-3xl font-bold text-navy md:text-4xl"><Sparkles className="mr-2 inline h-7 w-7 text-amber-500" />Visuels réseaux sociaux — {ctx.company.name}</h1>
      <p className="mt-1 text-neutral-600">Personnalisez et téléchargez vos visuels social media en quelques clics.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[400px_1fr]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-neutral-100 bg-white p-4">
            <label className="text-xs font-bold uppercase text-neutral-600">Format</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {VARIANTS.map((v) => (
                <button key={v.v} onClick={() => setVariant(v.v)} className={`rounded-xl border-2 px-3 py-2 text-left text-xs transition-all ${variant === v.v ? "border-wine-500 bg-wine-50" : "border-neutral-200 bg-white"}`} data-testid={`fmt-${v.v}`}>
                  <div className="font-bold text-neutral-900">{v.label}</div>
                  <div className="text-[10px] text-neutral-500">{v.w}×{v.h}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-neutral-100 bg-white p-4">
            <label className="text-xs font-bold uppercase text-neutral-600"><Palette className="mr-1 inline h-3 w-3" />Fond</label>
            <p className="mt-1 text-[10px] text-neutral-500">Par défaut, le fond « La WineTech » officiel est utilisé. Choisissez un dégradé personnalisé pour l&apos;écraser.</p>
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {COLOR_PRESETS.map((p) => (
                <button
                  key={p.name}
                  onClick={() => { setC1(p.c1); setC2(p.c2); }}
                  className={`h-12 rounded-lg border-2 transition-all ${c1 === p.c1 && c2 === p.c2 ? "border-neutral-900" : "border-transparent"}`}
                  style={p.c1 ? { background: `linear-gradient(135deg, #${p.c1}, #${p.c2})` } : { background: "linear-gradient(135deg, #C43D8F, #E33170)" }}
                  title={p.name}
                  data-testid={`color-${p.c1 || 'default'}`}
                />
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div><div className="text-[10px] uppercase text-neutral-500">Couleur 1</div><div className="flex items-center gap-1"><input type="color" value={`#${c1 || '000000'}`} onChange={(e) => setC1(e.target.value.replace("#", ""))} className="h-8 w-12 rounded border" /><input value={c1} onChange={(e) => setC1(e.target.value.replace("#", ""))} placeholder="défaut" className="w-20 rounded border border-neutral-200 px-2 py-1 text-xs uppercase" /></div></div>
              <div><div className="text-[10px] uppercase text-neutral-500">Couleur 2</div><div className="flex items-center gap-1"><input type="color" value={`#${c2 || '000000'}`} onChange={(e) => setC2(e.target.value.replace("#", ""))} className="h-8 w-12 rounded border" /><input value={c2} onChange={(e) => setC2(e.target.value.replace("#", ""))} placeholder="défaut" className="w-20 rounded border border-neutral-200 px-2 py-1 text-xs uppercase" /></div></div>
            </div>
          </div>

          <div className="rounded-2xl border border-neutral-100 bg-white p-4 space-y-3">
            <div>
              <label className="text-xs font-bold uppercase text-neutral-600">Pastille bleue (Pôle)</label>
              <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Ex. Pôle Producteur" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="visual-tag" />
              <div className="mt-1 flex flex-wrap gap-1">
                {POLE_TAG_PRESETS.map((p) => (
                  <button key={p} onClick={() => setTag(p)} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${tag === p ? "border-navy bg-navy text-white" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100"}`} data-testid={`pole-preset-${p.replace(/\s+/g, '-').toLowerCase()}`}>{p}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-neutral-600">Description (1 phrase)</label>
              <textarea value={slogan} onChange={(e) => setSlogan(e.target.value)} rows={2} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="visual-slogan" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-neutral-600">Sticker promo</label>
              <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Ex: -20% adhésion" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="visual-promo" />
              <div className="mt-1 flex flex-wrap gap-1">
                {PROMO_PRESETS.map((p) => <button key={p} onClick={() => setPromo(p)} className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800 hover:bg-amber-100" data-testid={`promo-${p}`}>{p}</button>)}
                <button onClick={() => setPromo("")} className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-bold text-neutral-500">Aucun</button>
              </div>
            </div>
            {ctx.contacts.length > 0 && (
              <div>
                <label className="text-xs font-bold uppercase text-neutral-600">Contact mis en avant</label>
                <select value={contactId} onChange={(e) => setContactId(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="visual-contact">
                  <option value="">— Aucun —</option>
                  {ctx.contacts.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.is_primary_contact ? "★" : ""}</option>)}
                </select>
              </div>
            )}
            <button onClick={reset} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-wine-700"><RotateCcw className="h-3 w-3" />Réinitialiser</button>
          </div>

          <a href={previewUrl} download={`lawinetech-${slug}-${variant}.png`} className="block w-full rounded-full bg-wine-600 px-5 py-3 text-center text-sm font-bold text-white hover:bg-wine-700" data-testid="visual-download"><Download className="mr-1 inline h-4 w-4" />Télécharger {cw}×{ch}</a>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-neutral-100 bg-neutral-50 p-4">
            <div className="text-[10px] uppercase text-neutral-500">Aperçu live</div>
            <div className="mt-2 overflow-hidden rounded-xl border border-neutral-200 bg-white">
              <img src={previewUrl} alt="Aperçu" className="block w-full" style={{ aspectRatio: ratio }} data-testid="visual-preview" />
            </div>
          </div>
          <EtOHStudioPublisher
            imageUrl={previewUrl}
            defaultText={ctx.company ? `🎉 Nouveau membre : ${ctx.company.name}\n\n${ctx.company.tagline || ctx.company.description || ""}\n\n🔗 Découvrez-les sur lawinetech.com/members/${ctx.company.slug}` : ""}
          />
        </div>
      </div>
    </PortalLayout>
  );
}
